import { useCallback } from "react";
import type { IssueRow } from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useIssueViewMutations } from "./use-issue-view-mutations";

/**
 * Commits ONE custom-field value on an issue — the single write path shared
 * by the issue view dialog and the issues table's custom-field cells.
 *
 * `undefined` is the clear signal: a clear deletes the value row
 * (clearIssueCustomField) rather than storing an empty value. A set is a
 * single-key `updateValues` merge (the server folds it into the map), with an
 * optimistic full-map response so the cell updates in the same frame and a
 * concurrent edit to another field can't be clobbered client-side.
 *
 * Callers pass the DTO-space value (already through `fieldDto`).
 */
export type CommitCustomFieldValue = (
  issue: Pick<IssueRow, "issueId" | "values">,
  customFieldId: string,
  value: unknown,
) => void;

export function useCustomFieldValueCommit(): CommitCustomFieldValue {
  const { updateValues, clearCustomField } = useIssueViewMutations();

  return useCallback<CommitCustomFieldValue>(
    (issue, customFieldId, value) => {
      const { issueId } = issue;
      const currentValues = issue.values ?? {};

      if (value === undefined) {
        const next = { ...currentValues };
        delete next[customFieldId];
        clearCustomField({
          variables: { issueId, fieldId: customFieldId },
          optimisticResponse: {
            clearIssueCustomField: {
              __typename: "Issue",
              id: `Issue:${issueId}`,
              issueId,
              values: next,
            },
          },
          // A rejection rolls the optimistic write back silently — the toast
          // is the only signal the user's edit did not stick.
          onError: (err) => toastMutationError(err, "Failed to clear field value"),
        });
        return;
      }

      updateValues({
        variables: {
          issueId,
          values: { [customFieldId]: value },
        },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue",
            id: `Issue:${issueId}`,
            issueId,
            values: { ...currentValues, [customFieldId]: value },
          },
        },
        onError: (err) => toastMutationError(err, "Failed to update field value"),
      });
    },
    [clearCustomField, updateValues],
  );
}
